import { CommonModule } from "@angular/common";
import {
  Component,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from "@angular/forms";

import {
  NgbCalendar,
  NgbDate,
  NgbDateParserFormatter,
  NgbDateStruct,
  NgbDatepickerModule,
  NgbDatepickerI18n,
  NgbInputDatepicker,
} from "@ng-bootstrap/ng-bootstrap";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { TranslationService } from "src/app/i18n";
import { LabelMode } from "src/utils/label-mode.enum";
import { GenerateRandom } from "src/utils/random";
import {
  CustomDateParserFormatter,
  CustomDatepickerI18n,
  getDatepickerYearOffset,
  LocaleAwareCalendar,
  serializeNgbDate,
  shiftNgbDateYear,
  toNgbDate,
} from "src/utils/datapicker-format";
import { KeeniconComponent } from "../keenicon/keenicon.component";

@Component({
  selector: "app-datepicker",
  templateUrl: "./datepicker.component.html",
  styleUrls: ["./datepicker.component.scss"],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbDatepickerModule,
    TranslateModule,
    KeeniconComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerComponent),
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
    { provide: NgbCalendar, useClass: LocaleAwareCalendar },
    { provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n },
  ],
})
export class DatepickerComponent
  implements OnInit, OnDestroy, ControlValueAccessor {
  @ViewChild("d") datepicker?: NgbInputDatepicker;

  @Input() name = GenerateRandom(6);
  @Input() label: string;
  @Input() addonLabel = "";
  @Input() placeholder = "";
  @Input() errorMessage = "VALIDATOR.REQUIRED";
  @Input() disabledDate: any;
  @Input() minYear = 1900;
  @Input() beYear = 543;
  @Input() classes: string;
  @Input() clearable = false;
  @Input() required = false;

  minPDate: NgbDateStruct;
  maxPDate: NgbDateStruct;
  currentYear: number;
  LabelMode = LabelMode;
  mode: LabelMode = LabelMode.DEFAULT;
  form = new FormControl<NgbDate | null>(null);
  isError = false;

  private readonly subscriptions = new Subscription();
  private minDateValue?: Date;
  private maxDateValue?: Date;
  private yearOffset: number;
  private readonly translationService = inject(TranslationService);
  private readonly translateService = inject(TranslateService);

  constructor() {
    this.yearOffset = getDatepickerYearOffset(
      this.translateService.currentLang,
      this.translationService.getSelectedLanguage(),
      this.beYear
    );
    this.currentYear = new Date().getFullYear() + this.yearOffset;
    this.updateDateBounds();
  }

  @Input()
  set isDisabled(value: boolean) {
    if (value) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  @Input()
  set error(value: any) {
    this.isError = false;
    if (value != null && value !== false) {
      this.isError = true;
    }
  }

  @Input() set minDate(value: any) {
    this.minDateValue = value != null ? new Date(value) : undefined;
    this.updateDateBounds();
  }

  @Input() set maxDate(value: any) {
    this.maxDateValue = value != null ? new Date(value) : undefined;
    this.updateDateBounds();
  }

  @Input() set labelMode(value: LabelMode) {
    this.mode = value;
  }

  onChange = (value: string | null) => {};
  onTouched = (value: string) => {};

  ngOnInit(): void {
    this.yearOffset = getDatepickerYearOffset(
      this.translateService.currentLang,
      this.translationService.getSelectedLanguage(),
      this.beYear
    );
    this.currentYear = new Date().getFullYear() + this.yearOffset;
    this.updateDateBounds();

    this.subscriptions.add(
      this.form.valueChanges.subscribe((value) => {
        if (value?.year != null) {
          if (value.year - this.yearOffset >= this.minYear) {
            this.onChange(serializeNgbDate(value, this.yearOffset));
          }
        }
        if (value === null) {
          this.onChange(value);
        }
      })
    );

    this.subscriptions.add(
      this.translateService.onLangChange.subscribe(() => {
        const previousOffset = this.yearOffset;
        this.yearOffset = getDatepickerYearOffset(
          this.translateService.currentLang,
          this.translationService.getSelectedLanguage(),
          this.beYear
        );
        this.currentYear = new Date().getFullYear() + this.yearOffset;
        this.updateDateBounds();

        const value = this.form.value;
        if (value?.year != null) {
          this.form.setValue(
            shiftNgbDateYear(value, previousOffset, this.yearOffset),
            { emitEvent: false }
          );
        }

        if (this.datepicker?.isOpen() === true) {
          this.datepicker.navigateTo(this.form.value ?? undefined);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  writeValue(obj: any): void {
    if (obj == null || obj === "") {
      this.form.reset(null, { emitEvent: false });
      return;
    }

    const pdate = new Date(obj);

    this.form.setValue(toNgbDate(pdate, this.yearOffset), { emitEvent: false });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {}

  private updateDateBounds(): void {
    const currentYear = new Date().getFullYear();
    this.minPDate = this.minDateValue
      ? toNgbDate(this.minDateValue, this.yearOffset)
      : new NgbDate(currentYear + this.yearOffset - 80, 12, 31);
    this.maxPDate = this.maxDateValue
      ? toNgbDate(this.maxDateValue, this.yearOffset)
      : new NgbDate(currentYear + this.yearOffset + 20, 12, 31);
  }
}
